/**
 * THE ROUTE GUARD, AS A COMPONENT AROUND A SCREEN.
 *
 * WHY A COMPONENT AND NOT `beforeLoad`. TanStack's `beforeLoad` runs before any React context
 * exists, and the session lives in React context - `AuthSessionProvider`, mounted by the root
 * route. Routing the session into the router's own context would give it two owners. So the
 * decision is made where the session already is: `./gate.ts` decides, this renders the answer, and
 * the route tree stays flat, which is what `test/router.spec.ts` reads off it.
 *
 * ★ THE DEFAULT BUILD IS NOT GATED AT ALL, and that is load-bearing. With the one live flag off
 * (`isLiveApiConfigured()`, src/config.ts) every screen renders its fixture exactly as it did before
 * this file existed - no session, no sign-in, no redirect - because the accessibility leg visits
 * those screens on the default build with nobody signed in. The branch is decided per build, so
 * the hook order inside each branch never changes between renders.
 *
 * A REDIRECT IS A `replace`, NOT A `push`: Back from `/sign-in` must not land on the screen that
 * sent the person there only to send them straight back.
 *
 * THREE PLACES IT SENDS PEOPLE, all decided in ./gate.ts: `/sign-in?next=` (signed out - including
 * a person whose credential was just refused on one of the screen's own requests, which
 * ./send-with-session.ts reports and the reducer turns into a sign-out), the `notReachablePath` the
 * router passes, and `/terms?next=` - the terms interstitial, the first time it is due in a visit.
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useRouter } from '@tanstack/react-router';
import { LoadingState } from '@moonx/ui';
import { isLiveApiConfigured } from '../../../config';
import { useTranslation } from '../../../i18n';
import { AuthShell } from '../components/AuthShell';
import { signInPathFor, termsPathFor } from '../navigation';
import { useAuthSession } from './AuthSessionProvider';
import { gateDecision } from './gate';
import type { GateRequirement } from './gate';

export interface SessionGateProps {
  readonly requirement: GateRequirement;
  /**
   * Where a signed-in person goes when the route is not theirs - a reader with no author row at a
   * cabinet screen goes to "Become an author" (`/welcome`). Passed in by the router.
   */
  readonly notReachablePath: string;
  /**
   * R-071: the wait below is the page's one `main` landmark. The router sets it for a screen drawn
   * inside `CabinetShell`, whose `AppShell.Main` is inside this gate and so not on screen while it
   * waits; a page outside the shell already has its `main` around the gate (`outsideTheShell`).
   */
  readonly main?: boolean;
  readonly children: ReactNode;
}

export function SessionGate(props: SessionGateProps) {
  if (!isLiveApiConfigured()) {
    return props.children;
  }
  return <LiveSessionGate {...props} />;
}

function LiveSessionGate({ requirement, notReachablePath, main, children }: SessionGateProps) {
  const { t } = useTranslation('auth');
  const { state } = useAuthSession();
  const router = useRouter();
  // The address to come back to: path and query, no hash. Read through the router so a memory
  // history in a test and the browser's history in the app give the same answer.
  const here = useLocation({ select: (location) => `${location.pathname}${location.searchStr}` });

  const decision = gateDecision(state, requirement);
  const target =
    decision.kind === 'sign-in'
      ? signInPathFor(here)
      : decision.kind === 'not-reachable'
        ? notReachablePath
        : decision.kind === 'terms'
          ? termsPathFor(here)
          : null;

  useEffect(() => {
    // ★ ONLY FROM THE GATED ADDRESS ITSELF. `useLocation` follows the router's PENDING location, so
    // after the replace below this gate renders once more AT `/sign-in?next=...` before it unmounts.
    // A second replace from there computes a target with no `next` - `safeReturnPath` refuses the
    // door itself as a way back - and drops it. Measured: three router-session cases saw `next: null`.
    if (target !== null && pathOf(target) !== pathOf(here)) {
      router.history.replace(target);
    }
  }, [router, target, here]);

  if (decision.kind === 'render') {
    return children;
  }

  // Waiting, or on the way somewhere else. Either way the screen underneath must not render: it
  // would fire its reads with no token, or with the token of somebody it is not for.
  const waiting = (
    <AuthShell title={t('opening.title')}>
      <LoadingState label={t('opening.loadingLabel')} lines={2} />
    </AuthShell>
  );
  return main === true ? <main>{waiting}</main> : waiting;
}

/** The pathname of a `path?query` string - the part the gate compares, since `next` rides the query. */
function pathOf(pathAndQuery: string): string {
  return pathAndQuery.split('?', 1)[0] ?? pathAndQuery;
}
